(function() {
  class ProductDatasheetGenerator {
    constructor() {
      this.buttonSelector = '[data-product-datasheet-button]';
      this.pdfLibraryUrl = 'https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js';
      this.svg2pdfLibraryUrl = 'https://cdn.jsdelivr.net/npm/svg2pdf.js@2.1.4/dist/svg2pdf.umd.min.js';
      this.pdfLibPromise = null;
      this.productDataPromise = null;
      this.logoPromise = null;
      this.svgPluginPromise = null;
      this.cachedImages = new Map();
      this.activeVariantId = null;
      this.colorProbeElement = null;
      this.finishesGuideUrl = 'https://ghidini1849.com/pages/finishes-guide';
    }

    init() {
      document.addEventListener('click', (event) => {
        const button = event.target.closest(this.buttonSelector);
        if (!button) {
          return;
        }
        event.preventDefault();
        this.handleButtonClick(button);
      });

      const variantEvents = ['variant:update', 'variant:change', 'variant:changed', 'variant:selected'];
      variantEvents.forEach((eventName) => {
        document.addEventListener(eventName, (event) => {
          const variant = event?.detail?.variant;
          const variantId = variant?.id || event?.detail?.variantId;
          if (variantId) {
            this.activeVariantId = parseInt(variantId, 10);
          }
        });
      });
    }

    async handleButtonClick(button) {
      if (button.disabled) {
        return;
      }

      button.disabled = true;
      button.classList.add('is-loading');

      try {
        const variantId = this.getActiveVariantId(button);
        const { product, variant } = await this.resolveVariant(variantId, button);
        if (!variant) {
          throw new Error('Unable to resolve variant for datasheet generation.');
        }

        const specRows = this.orderSpecificationRows(this.extractSpecificationRows(button));
        const variantInfoEntries = this.buildVariantInfo(product, variant);
        const images = await this.collectVariantImages(product, variant);
        const descriptionHtml = this.getProductDescriptionHtml(product);
        const logo = await this.loadLogoImage();
        const headingText = button.dataset.datasheetHeading || 'Product Datasheet';
        const websiteLinkLabel = button.dataset.websiteLinkLabel || '';
        const finishesGuideLabel = button.dataset.finishesGuideLabel || '';
        const pdfBytes = await this.buildPdf({
          product,
          variant,
          specRows,
          variantInfoEntries,
          images,
          descriptionHtml,
          logo,
          headingText,
          websiteLinkLabel,
          finishesGuideLabel,
        });
        const filename = this.getFilename(variant, button);
        this.triggerDownload(pdfBytes, filename);
      } catch (error) {
        console.error('[Datasheet] Generation failed', error);
      } finally {
        button.classList.remove('is-loading');
        button.disabled = false;
      }
    }

    getActiveVariantId(button) {
      const variantContainer = button.closest('[variant-id]');
      if (variantContainer && variantContainer.getAttribute('variant-id')) {
        return parseInt(variantContainer.getAttribute('variant-id'), 10);
      }

      const datasetId = button.dataset.variantId;
      if (datasetId) {
        return parseInt(datasetId, 10);
      }

      if (this.activeVariantId) {
        return this.activeVariantId;
      }

      const formInput = document.querySelector('form[action*="/cart/add"] input[name="id"]');
      if (formInput && formInput.value) {
        return parseInt(formInput.value, 10);
      }

      return null;
    }

    async resolveVariant(variantId, button) {
      const product = await this.fetchProductData();
      let resolvedVariant = null;
      if (variantId) {
        resolvedVariant = product.variants.find((variant) => parseInt(variant.id, 10) === parseInt(variantId, 10));
      }

      if (!resolvedVariant) {
        const datasetSku = button.dataset.variantSku;
        if (datasetSku) {
          resolvedVariant = product.variants.find((variant) => variant.sku === datasetSku);
        }
      }

      if (!resolvedVariant) {
        resolvedVariant = product.variants[0];
      }

      return { product, variant: resolvedVariant };
    }

    async fetchProductData() {
      if (!this.productDataPromise) {
        this.productDataPromise = new Promise(async (resolve, reject) => {
          try {
            const handle = this.getProductHandle();
            if (!handle) {
              throw new Error('Missing product handle');
            }
            const root = window.Shopify?.routes?.root || '/';
            const response = await fetch(`${root}products/${handle}.js`, { credentials: 'same-origin' });
            if (!response.ok) {
              throw new Error(`Failed to load product JSON for ${handle}`);
            }
            const productData = await response.json();
            productData.tags_array = this.parseTags(productData.tags);
            resolve(productData);
          } catch (error) {
            reject(error);
          }
        });
      }

      return this.productDataPromise;
    }

    getProductHandle() {
      const handleFromMeta = window.ShopifyAnalytics?.meta?.product?.handle || window.meta?.product?.handle;
      if (handleFromMeta) {
        return handleFromMeta;
      }

      const path = window.location.pathname || '';
      const index = path.indexOf('/products/');
      if (index !== -1) {
        const remainder = path.slice(index + 10);
        const handleFromPath = remainder.split('/')[0];
        if (handleFromPath) {
          return handleFromPath;
        }
      }

      const scopedHandleElement = document.querySelector(
        '.hdt-section--feature-product [data-product-handle], main [data-product-handle]'
      );
      if (scopedHandleElement?.dataset?.productHandle) {
        return scopedHandleElement.dataset.productHandle;
      }

      const handleElement = document.querySelector('[data-product-handle]');
      if (handleElement?.dataset?.productHandle) {
        return handleElement.dataset.productHandle;
      }

      return null;
    }

    parseTags(tags) {
      if (!tags) {
        return [];
      }
      if (Array.isArray(tags)) {
        return tags.map((tag) => `${tag}`.trim());
      }
      return `${tags}`.split(',').map((tag) => tag.trim()).filter(Boolean);
    }

    extractSpecificationRows(button) {
      const rows = [];
      const seen = new Set();
      const wrapper = button.closest('[variant-id]');
      const variantId = wrapper ? wrapper.getAttribute('variant-id') : button.dataset.variantId;

      const normalizeText = (value) => String(value || '').replace(/\s+/g, ' ').trim();
      const pushRow = (label, value) => {
        const normalizedLabel = normalizeText(label);
        const normalizedValue = normalizeText(value);
        if (!normalizedLabel || !normalizedValue) {
          return;
        }
        const rowKey = `${normalizedLabel.toLowerCase()}::${normalizedValue.toLowerCase()}`;
        if (seen.has(rowKey)) {
          return;
        }
        seen.add(rowKey);
        rows.push({ label: normalizedLabel, value: normalizedValue });
      };

      const collectLegacyRows = (root) => {
        root.querySelectorAll('table tbody tr').forEach((row) => {
          if (row.contains(button) || row.closest('.product-specs-section--downloads')) {
            return;
          }
          const cells = row.querySelectorAll('td');
          if (cells.length < 2) {
            return;
          }
          pushRow(cells[0].textContent, cells[1].textContent);
        });
      };

      const collectSpecRows = (root) => {
        root.querySelectorAll('.product-specs-row').forEach((row) => {
          if (row.contains(button) || row.closest('.product-specs-section--downloads')) {
            return;
          }
          const label = row.querySelector('.product-specs-label');
          const value = row.querySelector('.product-specs-value');
          if (!label || !value) {
            return;
          }
          pushRow(label.textContent, value.textContent);
        });
      };

      const collectBulbTables = (root) => {
        root.querySelectorAll('.product-specs-bulb-table').forEach((table) => {
          if (table.contains(button) || table.closest('.product-specs-section--downloads')) {
            return;
          }
          const tableLabel = normalizeText(table.getAttribute('aria-label')) || 'Required bulb';
          const headers = Array.from(table.querySelectorAll('.product-specs-bulb-table__row--header [role="columnheader"], .product-specs-bulb-table__row--header span'))
            .map((cell) => normalizeText(cell.textContent))
            .filter(Boolean);

          table.querySelectorAll('.product-specs-bulb-table__row:not(.product-specs-bulb-table__row--header)').forEach((row) => {
            const cells = Array.from(row.querySelectorAll('[role="cell"], span'))
              .map((cell) => normalizeText(cell.textContent))
              .filter(Boolean);
            if (!cells.length) {
              return;
            }

            if (headers.length === cells.length) {
              const value = cells.map((cell, index) => `${headers[index]}: ${cell}`).join('; ');
              pushRow(tableLabel, value);
              return;
            }

            pushRow(tableLabel, cells.join('; '));
          });
        });
      };

      const collectRows = (root) => {
        if (!root) {
          return;
        }
        collectLegacyRows(root);
        collectSpecRows(root);
        collectBulbTables(root);
      };

      const candidateWrappers = [];
      if (wrapper) {
        candidateWrappers.push(wrapper);
      }

      if (variantId) {
        document.querySelectorAll('[variant-id]').forEach((candidate) => {
          if (candidate.getAttribute('variant-id') !== String(variantId)) {
            return;
          }
          if (!candidateWrappers.includes(candidate)) {
            candidateWrappers.push(candidate);
          }
        });
      }

      candidateWrappers.forEach((candidate) => collectRows(candidate));

      if (rows.length || candidateWrappers.length) {
        return rows;
      }

      collectRows(document);

      return rows;
    }

    orderSpecificationRows(rows) {
      if (!Array.isArray(rows) || !rows.length) {
        return [];
      }

      const preparedRows = rows
        .map((row, index) => ({
          label: row.label,
          value: row.value,
          index,
          priority: this.getSpecificationRowPriority(row.label),
        }));
      const hasRequiredBulbRow = preparedRows.some((row) => this.isRequiredBulbRow(row.label));
      const hasWattageSocketRow = preparedRows.some((row) => this.isWattageSocketRow(row.label));

      return preparedRows
        .filter((row) => !this.shouldSkipSpecificationRow(row.label))
        .filter((row) => !(hasWattageSocketRow && this.isRequiredBulbRow(row.label)))
        .filter((row) => !(hasRequiredBulbRow && !hasWattageSocketRow && this.isRequiredBulbSourceRow(row.label)))
        .sort((a, b) => {
          if (a.priority !== b.priority) {
            return a.priority - b.priority;
          }
          return a.index - b.index;
        })
        .map(({ label, value }) => ({ label, value }));
    }

    normalizeSpecificationLabel(label) {
      return String(label || '')
        .toLowerCase()
        .replace(/ß/g, 'ss')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    }

    shouldSkipSpecificationRow(label) {
      const normalized = this.normalizeSpecificationLabel(label);
      if (!normalized) {
        return true;
      }

      const skipLabels = [
        'download product datasheet',
        'scarica scheda prodotto',
        'telecharger la fiche produit',
        'produktdatenblatt herunterladen',
        'product datasheet',
        'scheda prodotto',
        'fiche produit',
        'produktdatenblatt',
        'link to product page',
        'link alla pagina prodotto',
        'lien vers la page produit',
        'link zur produktseite',
        'finishes guide',
        'guida finiture',
        'guide des finitions',
        'oberflachen guide',
      ];

      return skipLabels.includes(normalized);
    }

    isRequiredBulbRow(label) {
      const normalized = this.normalizeSpecificationLabel(label);
      const requiredBulbLabels = ['required bulb', 'lampadina richiesta', 'ampoule requise', 'benotigtes leuchtmittel'];
      return requiredBulbLabels.some((candidate) => normalized === candidate || normalized.includes(candidate));
    }

    isWattageSocketRow(label) {
      const normalized = this.normalizeSpecificationLabel(label);
      const wattageSocketLabels = ['wattage socket', 'potenza attacco', 'puissance douille', 'leistung fassung'];
      return wattageSocketLabels.some((candidate) => normalized === candidate || normalized.includes(candidate));
    }

    isRequiredBulbSourceRow(label) {
      const normalized = this.normalizeSpecificationLabel(label);
      const sourceLabels = [
        'number of lights',
        'numero di luci',
        'nombre de lumieres',
        'anzahl der leuchten',
        'wattage socket',
        'potenza attacco',
        'puissance douille',
        'leistung fassung',
        'wattage',
        'potenza',
        'puissance',
        'leistung',
        'socket',
        'attacco',
        'douille',
        'fassung',
      ];
      return sourceLabels.some((candidate) => normalized === candidate || normalized.includes(candidate));
    }

    getSpecificationRowPriority(label) {
      const normalized = this.normalizeSpecificationLabel(label);
      const groups = [
        { priority: 10, labels: ['item no', 'codice articolo', 'reference', 'artikelnummer'] },
        { priority: 20, labels: ['materials', 'materiali', 'materiaux', 'materialien'] },
        { priority: 21, labels: ['colors', 'colori', 'couleurs', 'farben'] },
        { priority: 22, labels: ['weight', 'peso', 'poids', 'gewicht'] },
        { priority: 30, labels: ['base'] },
        { priority: 31, labels: ['height', 'altezza', 'hauteur', 'hohe'] },
        { priority: 32, labels: ['depth', 'profondita', 'profondeur', 'tiefe'] },
        { priority: 33, labels: ['width', 'larghezza', 'largeur', 'breite'] },
        { priority: 34, labels: ['diameter', 'diametro', 'diametre', 'durchmesser'] },
        { priority: 35, labels: ['metal lampshades', 'paralumi in metallo', 'abat jours en metal', 'metallschirme'] },
        { priority: 36, labels: ['hole spacing', 'interasse', 'entraxe', 'lochabstand'] },
        { priority: 40, labels: ['integrated led', 'led integrato', 'led integree', 'integrierte led'] },
        { priority: 41, labels: ['number of lights', 'numero di luci', 'nombre de lumieres', 'anzahl der leuchten'] },
        { priority: 42, labels: ['wattage socket', 'potenza attacco', 'puissance douille', 'leistung fassung'] },
        { priority: 43, labels: ['wattage', 'potenza', 'puissance', 'leistung'] },
        { priority: 44, labels: ['socket', 'attacco', 'douille', 'fassung'] },
        { priority: 45, labels: ['light temperature', 'temperatura colore', 'temperature de la lumiere', 'lichttemperatur'] },
        { priority: 46, labels: ['luminous flux', 'flusso luminoso', 'flux lumineux', 'lichtstrom'] },
        { priority: 47, labels: ['led lifespan', 'durata del led', 'duree de vie de la led', 'led lebensdauer'] },
        { priority: 48, labels: ['dimmable', 'dimmerabile', 'dimmbar'] },
        { priority: 49, labels: ['dimmer included', 'dimmer incluso', 'variateur inclus', 'dimmer enthalten'] },
        { priority: 50, labels: ['light bulbs included', 'lampadine incluse', 'ampoules incluses', 'leuchtmittel enthalten'] },
        { priority: 51, labels: ['required bulb', 'lampadina richiesta', 'ampoule requise', 'benotigtes leuchtmittel'] },
        { priority: 60, labels: ['voltage range', 'gamma di tensione', 'plage de tension', 'spannungsbereich'] },
        { priority: 61, labels: ['voltage', 'tensione', 'tension', 'spannung'] },
        { priority: 62, labels: ['energy class', 'classe energetica', 'classe energetique', 'energieklasse'] },
        { priority: 63, labels: ['ip code', 'grado di protezione ip', 'indice de protection ip', 'ip code'] },
        { priority: 64, labels: ['protection class', 'classe di protezione', 'classe de protection', 'schutzklasse'] },
        { priority: 70, labels: ['ceramic elements', 'elementi in ceramica', 'elements en ceramique', 'keramikelemente'] },
        { priority: 71, labels: ['crystal elements', 'elementi in cristallo', 'elements en cristal', 'kristallelemente'] },
        { priority: 72, labels: ['fabric elements', 'elementi in tessuto', 'elements en tissu', 'stoffelemente'] },
        { priority: 73, labels: ['glass elements', 'elementi in vetro', 'elements en verre', 'glaselemente'] },
        { priority: 80, labels: ['cable', 'cavo', 'cable', 'kabel'] },
        { priority: 81, labels: ['tap included', 'rubinetto incluso', 'robinet inclus', 'wasserhahn enthalten'] },
        { priority: 90, labels: ['notes', 'note', 'remarques', 'hinweise'] },
      ];

      for (const group of groups) {
        if (group.labels.some((candidate) => normalized === candidate || normalized.includes(candidate))) {
          return group.priority;
        }
      }

      return 200;
    }

    buildVariantInfo(product, variant) {
      const getOptionLabel = (index) => {
        const optionSources = [product.options, product.options_with_values];
        for (const source of optionSources) {
          if (!Array.isArray(source) || typeof index !== 'number') {
            continue;
          }
          const entry = source[index];
          if (typeof entry === 'string') {
            return entry;
          }
          if (entry && typeof entry === 'object') {
            if (typeof entry.name === 'string') {
              return entry.name;
            }
            if (typeof entry.label === 'string') {
              return entry.label;
            }
          }
        }
        return '';
      };

      const pickerRoot = document.querySelector('hdt-variant-picker');
      const entries = [];

      (variant.options || []).forEach((value, index) => {
        const rawLabel = getOptionLabel(index);
        const label = String(rawLabel || '').trim();
        const valueText = value != null ? String(value).trim() : '';
        if (!label || !valueText || valueText.toLowerCase() === 'default') {
          return;
        }
        const showFinishesGuide = this.shouldShowFinishesGuideLink(label);
        const baseSwatch = this.resolveOptionSwatch(index, valueText, pickerRoot);
        const swatch = showFinishesGuide ? this.resolveFinishSwatchFallback(baseSwatch, variant) : baseSwatch;
        entries.push({
          label,
          value: valueText,
          text: `${label}: ${valueText}`,
          swatch,
          showFinishesGuide,
        });
      });

      return entries;
    }

    shouldShowFinishesGuideLink(label) {
      if (!label) {
        return false;
      }

      const normalizedLabel = String(label).trim().toLocaleLowerCase();
      if (!normalizedLabel) {
        return false;
      }

      const candidates = ['finish', 'finition', 'finitura', 'oberfläche'];
      return candidates.some((candidate) => normalizedLabel === candidate.toLocaleLowerCase());
    }

    resolveOptionSwatch(optionIndex, value, pickerRoot) {
      if (typeof optionIndex !== 'number' || optionIndex < 0) {
        return null;
      }

      const primaryRoot = pickerRoot instanceof Element ? pickerRoot : null;
      const fallbackRoot = document.querySelector('hdt-variant-picker');
      const searchRoot = primaryRoot || fallbackRoot || document;
      const fieldset = searchRoot.querySelector ? searchRoot.querySelector(`[data-index="${optionIndex}"]`) : null;
      if (!fieldset) {
        return null;
      }

      const typeAttr = (fieldset.getAttribute('type') || '').toLowerCase();
      const isColorType = typeAttr.includes('color') || fieldset.classList.contains('is-style-color');
      if (!isColorType) {
        return null;
      }

      let label = null;
      const checkedInput = fieldset.querySelector('input[is-value][type="radio"]:checked');
      if (checkedInput) {
        const selector = `label[for="${this.cssEscape(checkedInput.id)}"]`;
        label = fieldset.querySelector(selector);
      }

      const valueString = value != null ? String(value).trim() : '';
      if (!label && valueString) {
        const escapedValue = this.cssEscape(valueString);
        const candidateSelectors = [
          `[data-value="${escapedValue}"]`,
          `[value="${escapedValue}"]`,
          `[aria-label="${escapedValue}"]`,
        ];
        for (const selector of candidateSelectors) {
          const candidate = fieldset.querySelector(selector);
          if (candidate) {
            label = candidate.closest('label') || candidate;
            break;
          }
        }
      }

      if (!label) {
        label =
          fieldset.querySelector('.hdt-product-form_value[aria-checked="true"]') ||
          fieldset.querySelector('.hdt-product-form_value.is-active') ||
          fieldset.querySelector('.hdt-product-form_value[aria-selected="true"]');
      }

      if (!label && valueString) {
        const normalizedValue = this.normalizeOptionText(valueString);
        const labelCandidates = Array.from(fieldset.querySelectorAll('label, .hdt-product-form_value'));
        label = labelCandidates.find((candidate) => this.normalizeOptionText(candidate.textContent).includes(normalizedValue));
      }

      if (!label) {
        return null;
      }

      const swatch = {};
      const swatchImage = label.querySelector('img');
      if (swatchImage) {
        const imageSource = this.resolveImageSource(swatchImage);
        if (imageSource) {
          swatch.imageUrl = this.ensureImageWidth(imageSource, 256);
        }
        swatch.imageElement = swatchImage;
      }

      const swatchPattern = label.querySelector('.hdt-form-color-pattern, .hdt-block-swatch__color');
      if (!swatchPattern) {
        return Object.keys(swatch).length ? swatch : null;
      }

      const computed = window.getComputedStyle(swatchPattern);
      const inlineColorCandidate =
        swatchPattern.style.getPropertyValue('--hdt-bg-color') ||
        computed.getPropertyValue('--hdt-bg-color') ||
        swatchPattern.style.backgroundColor ||
        computed.backgroundColor;
      const normalizedColor = this.normalizeColor(inlineColorCandidate);

      const backgroundCandidate =
        swatchPattern.style.getPropertyValue('--hdt-bg-image') ||
        computed.getPropertyValue('--hdt-bg-image') ||
        swatchPattern.style.backgroundImage ||
        computed.backgroundImage;
      const imageUrl = this.extractUrlFromStyle(backgroundCandidate);

      if (imageUrl) {
        swatch.imageUrl = this.ensureImageWidth(imageUrl, 256);
      }
      if (normalizedColor) {
        swatch.color = normalizedColor;
      }

      return Object.keys(swatch).length ? swatch : null;
    }

    normalizeOptionText(value) {
      return String(value || '')
        .trim()
        .toLocaleLowerCase()
        .replace(/\s+/g, ' ');
    }

    resolveFinishSwatchFallback(swatch, variant) {
      const fallbackImageUrl = this.getFinishSwatchUrlFromSku(variant?.sku, 256);
      if (!fallbackImageUrl) {
        return swatch;
      }

      const nextSwatch = swatch && typeof swatch === 'object' ? { ...swatch } : {};
      if (!nextSwatch.imageUrl) {
        nextSwatch.imageUrl = fallbackImageUrl;
      }
      nextSwatch.fallbackImageUrl = fallbackImageUrl;
      return nextSwatch;
    }

    getFinishSwatchUrlFromSku(sku, width = 256) {
      const match = String(sku || '').trim().toLocaleUpperCase().match(/-([A-Z0-9]{3})$/);
      if (!match) {
        return null;
      }

      const finishCodes = ['OAS', 'OBM', 'OBR', 'OBS', 'OBV', 'OCR', 'OLV', 'ONO', 'ORO'];
      const finishCode = match[1];
      if (!finishCodes.includes(finishCode)) {
        return null;
      }

      const origin =
        window.location?.origin && window.location.origin !== 'null' ? window.location.origin : 'https://ghidini1849.com';
      return `${origin}/cdn/shop/files/${finishCode}-3-Ghidini-1849-Finish.jpg?width=${width}`;
    }

    cssEscape(value) {
      if (typeof value !== 'string') {
        return '';
      }
      if (window.CSS && typeof window.CSS.escape === 'function') {
        return window.CSS.escape(value);
      }
      return value.replace(/"/g, '\\"').replace(/\\/g, '\\\\');
    }

    extractUrlFromStyle(value) {
      if (!value || typeof value !== 'string') {
        return null;
      }
      // Normalize and strip surrounding quotes
      const trimmed = value.trim().replace(/^["']|["']$/g, '');
      if (!trimmed || trimmed.toLowerCase() === 'none') {
        return null;
      }

      // If Safari returns a plain URL (no url(...))
      if (/^https?:\/\//i.test(trimmed)) {
        return trimmed;
      }
      // Handle protocol-relative URLs starting with //
      if (/^\/\//.test(trimmed)) {
        return (window.location?.protocol || 'https:') + trimmed;
      }

      // Standard css url("...") syntax
      const match = trimmed.match(/url\((['"]?)(.*?)\1\)/i);
      if (match && match[2]) {
        let url = match[2].trim().replace(/^["']|["']$/g, '');
        if (/^\/\//.test(url)) {
          url = (window.location?.protocol || 'https:') + url;
        }
        return url || null;
      }

      // Safari can expose image-set(...) values without a simple url(...) wrapper.
      const rawUrlMatch = trimmed.match(/(?:https?:)?\/\/[^"')\s]+/i);
      if (rawUrlMatch && rawUrlMatch[0]) {
        let url = rawUrlMatch[0].trim().replace(/^["']|["']$/g, '');
        if (/^\/\//.test(url)) {
          url = (window.location?.protocol || 'https:') + url;
        }
        return url || null;
      }

      return null;
    }

    normalizeColor(color) {
      if (!color || typeof color !== 'string') {
        return null;
      }
      const trimmed = color.trim();
      if (!trimmed) {
        return null;
      }
      if (!this.colorProbeElement) {
        this.colorProbeElement = document.createElement('span');
      }
      const probe = this.colorProbeElement;
      const previous = probe.style.color;
      probe.style.color = '';
      probe.style.color = trimmed;
      const resolved = probe.style.color;
      probe.style.color = previous;
      if (!resolved || resolved === 'transparent' || resolved === 'rgba(0, 0, 0, 0)') {
        return null;
      }
      return resolved;
    }

    colorStringToRgb(color) {
      if (!color || typeof color !== 'string') {
        return null;
      }
      const value = color.trim().toLowerCase();
      if (!value || value === 'transparent' || value === 'none') {
        return null;
      }

      const hexMatch = value.match(/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i);
      if (hexMatch) {
        let hex = hexMatch[1];
        if (hex.length === 3) {
          hex = hex.split('').map((char) => char + char).join('');
        }
        if (hex.length === 8) {
          const alpha = parseInt(hex.slice(6, 8), 16) / 255;
          if (!Number.isFinite(alpha) || alpha <= 0) {
            return null;
          }
          hex = hex.slice(0, 6);
        }
        if (hex.length === 6) {
          const r = parseInt(hex.slice(0, 2), 16);
          const g = parseInt(hex.slice(2, 4), 16);
          const b = parseInt(hex.slice(4, 6), 16);
          if ([r, g, b].some((component) => Number.isNaN(component))) {
            return null;
          }
          return [r, g, b];
        }
      }

      const rgbMatch = value.match(/^rgba?\(([^)]+)\)$/);
      if (rgbMatch) {
        const parts = rgbMatch[1].split(',').map((part) => part.trim());
        if (parts.length < 3) {
          return null;
        }
        const parseComponent = (input) => {
          if (input.endsWith('%')) {
            const percentage = parseFloat(input);
            if (Number.isNaN(percentage)) {
              return null;
            }
            return Math.round(Math.min(100, Math.max(0, percentage)) * 2.55);
          }
          const number = parseFloat(input);
          if (Number.isNaN(number)) {
            return null;
          }
          return Math.round(Math.min(255, Math.max(0, number)));
        };
        const r = parseComponent(parts[0]);
        const g = parseComponent(parts[1]);
        const b = parseComponent(parts[2]);
        if ([r, g, b].some((component) => component == null)) {
          return null;
        }
        let alpha = 1;
        if (parts.length >= 4) {
          alpha = parseFloat(parts[3]);
          if (Number.isNaN(alpha)) {
            alpha = 1;
          }
        }
        if (alpha <= 0) {
          return null;
        }
        return [r, g, b];
      }

      return null;
    }

    getVariantProductUrl(product, variant) {
      const variantId = variant?.id;
      let baseUrl = '';
      if (product && typeof product.url === 'string' && product.url.trim()) {
        baseUrl = product.url.trim();
      } else if (product?.handle) {
        const root = window.Shopify?.routes?.root || '/';
        const normalizedRoot = (() => {
          if (typeof root !== 'string' || !root) {
            return '/';
          }
          let adjusted = root;
          if (!adjusted.startsWith('/')) {
            adjusted = `/${adjusted}`;
          }
          if (!adjusted.endsWith('/')) {
            adjusted = `${adjusted}/`;
          }
          return adjusted;
        })();
        baseUrl = `${normalizedRoot}products/${product.handle}`;
      } else {
        const handle = this.getProductHandle();
        if (handle) {
          const root = window.Shopify?.routes?.root || '/';
          const normalizedRoot = (() => {
            if (typeof root !== 'string' || !root) {
              return '/';
            }
            let adjusted = root;
            if (!adjusted.startsWith('/')) {
              adjusted = `/${adjusted}`;
            }
            if (!adjusted.endsWith('/')) {
              adjusted = `${adjusted}/`;
            }
            return adjusted;
          })();
          baseUrl = `${normalizedRoot}products/${handle}`;
        }
      }

      if (!baseUrl) {
        return '';
      }

      try {
        const resolved = new URL(baseUrl, window.location.origin);
        if (variantId) {
          resolved.searchParams.set('variant', variantId);
        }
        return resolved.toString();
      } catch (error) {
        let fallback = baseUrl;
        if (variantId) {
          fallback += (fallback.includes('?') ? '&' : '?') + `variant=${variantId}`;
        }
        return fallback;
      }
    }

    getProductDescriptionHtml(product) {
      const candidates = [
        product?.description_html,
        product?.body_html,
        product?.description,
      ];

      for (const candidate of candidates) {
        if (typeof candidate === 'string' && candidate.trim()) {
          return candidate;
        }
      }

      const selectors = [
        '.hdt-product-description [data-content]',
        '.hdt-product-description',
        '[data-product-description]',
        '.product__description',
      ];

      for (const selector of selectors) {
        const element = document.querySelector(selector);
        if (element && typeof element.innerHTML === 'string' && element.innerHTML.trim()) {
          return element.innerHTML;
        }
      }

      return '';
    }

    async collectVariantImages(product, variant) {
      const mainMediaRoot = document.querySelector('.hdt-product-media__main') || document;
      const mediaItems = [];
      const seen = new Set();
      mainMediaRoot.querySelectorAll('.hdt-product__media-item[data-media-id][data-media-type="image"]').forEach((item) => {
        const mediaId = parseInt(item.getAttribute('data-media-id'), 10);
        if (!mediaId || seen.has(mediaId)) {
          return;
        }
        seen.add(mediaId);
        const img = item.querySelector('img');
        if (!img) {
          return;
        }
        const groupName = item.getAttribute('data-group-name') || '';
        const groupValue = item.getAttribute('data-group-value') || '';
        const optionIndex = this.parseGroupOptionIndex(groupName);
        let conditions = null;
        const rawConditions = item.getAttribute('data-group-conditions');
        if (rawConditions) {
          try {
            const parsed = JSON.parse(rawConditions);
            conditions = Array.isArray(parsed) ? parsed : [];
          } catch {
            conditions = [];
          }
        }
        mediaItems.push({
          mediaId,
          img,
          optionIndex,
          conditions,
          groupValue: groupValue.trim(),
        });
      });

      if (!mediaItems.length) {
        return [];
      }

      const optionValues = (variant.options || []).map((value) => `${value}`.trim().toLowerCase());
      const selectedItems = mediaItems.filter((item) => {
        if (item.conditions !== null) {
          return item.conditions.length > 0 && item.conditions.every((condition) => {
            const index = condition?.option;
            return Number.isInteger(index) && index >= 0 &&
              typeof condition.value === 'string' &&
              optionValues[index] === condition.value.trim().toLowerCase();
          });
        }
        if (item.optionIndex === null || !item.groupValue) {
          return true;
        }
        return optionValues[item.optionIndex] === item.groupValue.toLowerCase();
      });

      const prepared = [];
      for (const item of selectedItems) {
        try {
          const imageData = await this.prepareImage(item.img, { mimeType: 'image/jpeg', maxWidth: 1500, quality: 0.82 });
          if (imageData) {
            prepared.push(imageData);
          }
        } catch (error) {
          console.warn('[Datasheet] Unable to process image', error);
        }
      }

      return prepared;
    }

    parseGroupOptionIndex(groupName) {
      if (!groupName || groupName.indexOf('option') !== 0) {
        return null;
      }
      const index = parseInt(groupName.replace('option', ''), 10);
      return Number.isNaN(index) ? null : index;
    }

    async loadPdfLibrary() {
      if (!this.pdfLibPromise) {
        this.pdfLibPromise = new Promise((resolve, reject) => {
          const resolveWithLibrary = () => {
            if (window.jspdf?.jsPDF) {
              resolve(window.jspdf.jsPDF);
            } else {
              reject(new Error('jsPDF failed to load'));
            }
          };

          if (window.jspdf?.jsPDF) {
            resolveWithLibrary();
            return;
          }

          const script = document.createElement('script');
          script.src = this.pdfLibraryUrl;
          script.async = true;
          script.onload = resolveWithLibrary;
          script.onerror = () => reject(new Error('Failed to load jsPDF library'));
          document.head.appendChild(script);
        });
      }

      const jsPDFConstructor = await this.pdfLibPromise;
      await this.ensureSvgPlugin();
      return jsPDFConstructor;
    }

    async ensureSvgPlugin() {
      const hasSvgSupport = () => Boolean(
        window.svg2pdf || (window.jspdf?.jsPDF?.API && typeof window.jspdf.jsPDF.API.svg === 'function')
      );

      if (hasSvgSupport()) {
        return;
      }

      if (!this.svgPluginPromise) {
        this.svgPluginPromise = new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = this.svg2pdfLibraryUrl;
          script.async = true;
          script.onload = () => resolve();
          script.onerror = () => reject(new Error('Failed to load svg2pdf library'));
          document.head.appendChild(script);
        });
      }

      try {
        await this.svgPluginPromise;
      } catch (error) {
        console.warn('[Datasheet] Failed to load svg2pdf helper', error);
        this.svgPluginPromise = null;
      }
    }

    async buildPdf({
      product,
      variant,
      specRows,
      variantInfoEntries,
      images,
      descriptionHtml,
      logo,
      headingText,
      websiteLinkLabel,
      finishesGuideLabel,
    }) {
      const jsPDFConstructor = await this.loadPdfLibrary();
      const doc = new jsPDFConstructor({ unit: 'pt', format: 'a4', compress: true });
      const margin = 40;
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      doc.setFont('helvetica', 'normal');

      const headerState = await this.renderHeader(doc, {
        margin,
        pageWidth,
        logo,
        headingText,
        sku: this.getVariantSkuDisplay(variant),
      });

      let cursorY = headerState.contentTop;

      const brandSuffix = ' | Ghidini 1849';
      const baseTitle = typeof product.title === 'string' ? product.title : '';
      const displayTitle = baseTitle.endsWith(brandSuffix)
        ? baseTitle
        : `${baseTitle}${brandSuffix}`;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setLineHeightFactor(1.25);
      const titleLines = doc.splitTextToSize(displayTitle.trim(), pageWidth - margin * 2);
      const titleLineHeight = doc.getFontSize() * doc.getLineHeightFactor();
      const titleStartY = cursorY + titleLineHeight - 6;
      titleLines.forEach((line, index) => {
        const lineY = titleStartY + index * titleLineHeight;
        doc.text(line, margin, lineY);
      });
      cursorY += titleLines.length * titleLineHeight + 12;

      doc.setFont('helvetica', 'normal');

      doc.setFontSize(10);
      doc.setLineHeightFactor(1.2);
      const infoLineHeight = 14;
      const infoEntries = Array.isArray(variantInfoEntries) ? variantInfoEntries : [];
      for (const entry of infoEntries) {
        const text = entry?.text ? String(entry.text).trim() : '';
        if (!text) {
          continue;
        }
        const swatchAsset = await this.prepareSwatchAsset(entry.swatch);
        const hasSwatch = Boolean(swatchAsset);
        const swatchSize = hasSwatch ? 20 : 0;
        const swatchGap = hasSwatch ? 10 : 0;
        const textX = hasSwatch ? margin + swatchSize + swatchGap : margin;
        const usableWidth = pageWidth - textX - margin;
        const contentWidth = usableWidth > 0 ? usableWidth : pageWidth - margin * 2;
        const infoLines = doc.splitTextToSize(text, contentWidth);
        const textLineCount = infoLines.length || (text ? 1 : 0);
        const lastLineWidth = infoLines.length ? doc.getTextWidth(infoLines[infoLines.length - 1]) : 0;
        const linkBaseSpacing = 0;

        let finishesLink = null;
        if (entry.showFinishesGuide && this.finishesGuideUrl) {
          const linkLabelRaw = typeof finishesGuideLabel === 'string' ? finishesGuideLabel.trim() : '';
          const linkLabel = String(linkLabelRaw || 'Finishes Guide').trim();
          if (linkLabel) {
            const originalFontSize = doc.getFontSize();
            const originalLineHeightFactor = typeof doc.getLineHeightFactor === 'function' ? doc.getLineHeightFactor() : 1.2;
            doc.setFontSize(9);
            doc.setLineHeightFactor(1.2);
            const prefixText = '   (';
            const suffixText = ')';
            const prefixWidth = doc.getTextWidth(prefixText);
            const labelWidth = doc.getTextWidth(linkLabel);
            const suffixWidth = doc.getTextWidth(suffixText);
            const totalWidth = prefixWidth + labelWidth + suffixWidth;
            const availableWidth = contentWidth - (lastLineWidth + linkBaseSpacing);
            const onNewLine = availableWidth <= 0 || totalWidth > availableWidth;
            finishesLink = {
              prefixText,
              suffixText,
              labelText: linkLabel,
              prefixWidth,
              labelWidth,
              suffixWidth,
              totalWidth,
              onNewLine,
            };
            doc.setFontSize(originalFontSize);
            doc.setLineHeightFactor(originalLineHeightFactor);
          }
        }

        const totalTextLines = textLineCount + (finishesLink && finishesLink.onNewLine ? 1 : 0);
        const entryBodyHeight = Math.max(infoLineHeight, totalTextLines * infoLineHeight, swatchSize);

        const textTopOffset = Math.max(0, (entryBodyHeight - totalTextLines * infoLineHeight) / 2);
        let lineY = cursorY + textTopOffset + infoLineHeight;
        infoLines.forEach((infoLine) => {
          doc.text(infoLine, textX, lineY);
          lineY += infoLineHeight;
        });

        if (finishesLink) {
          const baseLines = textLineCount || 1;
          const originalFontSize = doc.getFontSize();
          const originalLineHeightFactor = typeof doc.getLineHeightFactor === 'function' ? doc.getLineHeightFactor() : 1.2;
          doc.setFontSize(9);
          doc.setLineHeightFactor(1.2);
          doc.setTextColor(102, 102, 102);
          const linkLineY = cursorY + textTopOffset + (finishesLink.onNewLine ? baseLines + 1 : baseLines) * infoLineHeight;
          const linkX = finishesLink.onNewLine ? textX : textX + lastLineWidth + linkBaseSpacing;
          let currentX = linkX;
          if (finishesLink.prefixText) {
            doc.text(finishesLink.prefixText, currentX, linkLineY);
            currentX += finishesLink.prefixWidth;
          }
          if (finishesLink.labelText) {
            if (typeof doc.textWithLink === 'function') {
              doc.textWithLink(finishesLink.labelText, currentX, linkLineY, { url: this.finishesGuideUrl });
            } else {
              doc.text(finishesLink.labelText, currentX, linkLineY);
            }
            const underlineY = linkLineY + 1;
            doc.setLineWidth(0.4);
            doc.setDrawColor(102, 102, 102);
            doc.line(currentX, underlineY, currentX + finishesLink.labelWidth, underlineY);
            currentX += finishesLink.labelWidth;
          }
          if (finishesLink.suffixText) {
            doc.text(finishesLink.suffixText, currentX, linkLineY);
            currentX += finishesLink.suffixWidth;
          }
          doc.setTextColor(0, 0, 0);
          doc.setDrawColor(0, 0, 0);
          doc.setFontSize(originalFontSize);
          doc.setLineHeightFactor(originalLineHeightFactor);
        }

        if (hasSwatch) {
          const swatchX = margin;
          const swatchY = cursorY + Math.max(0, (entryBodyHeight - swatchSize) / 2);
          this.drawSwatch(doc, swatchX, swatchY, swatchSize, swatchAsset);
        }

        const entryHeight = entryBodyHeight + 8;
        cursorY += entryHeight;
      }

      const productUrl = this.getVariantProductUrl(product, variant);
      const websiteLabelRaw = typeof websiteLinkLabel === 'string' ? websiteLinkLabel.trim() : '';
      const websiteLabel = websiteLabelRaw || 'Website link';
      if (productUrl) {
        const originalFontSize = doc.getFontSize();
        const originalLineHeightFactor = typeof doc.getLineHeightFactor === 'function' ? doc.getLineHeightFactor() : 1.2;
        doc.setFontSize(9);
        doc.setLineHeightFactor(1.2);
        doc.setTextColor(102, 102, 102);
        const linkLineHeight = doc.getFontSize() * doc.getLineHeightFactor();
        const linkTopSpacing = 10;
        const linkX = margin;
        const linkY = cursorY + linkTopSpacing + linkLineHeight;
        if (typeof doc.textWithLink === 'function') {
          doc.textWithLink(websiteLabel, linkX, linkY, { url: productUrl });
        } else {
          doc.text(websiteLabel, linkX, linkY);
        }
        const linkWidth = doc.getTextWidth(websiteLabel);
        const underlineY = linkY + 1;
        doc.setLineWidth(0.4);
        doc.setDrawColor(102, 102, 102);
        doc.line(linkX, underlineY, linkX + linkWidth, underlineY);
        doc.setTextColor(0, 0, 0);
        doc.setDrawColor(0, 0, 0);
        doc.setFontSize(originalFontSize);
        doc.setLineHeightFactor(originalLineHeightFactor);
        cursorY = linkY + 8;
      }

      const layoutContext = {
        margin,
        pageWidth,
        pageHeight,
        headerState,
      };

      const sectionSpacing = 18;
      const descriptionToTableSpacing = infoLineHeight;

      if (images.length) {
        cursorY += sectionSpacing;
        cursorY = await this.renderImages(doc, images, cursorY, layoutContext);
        cursorY += sectionSpacing;
      } else if (!descriptionHtml && specRows.length) {
        cursorY += sectionSpacing;
      }

      if (descriptionHtml) {
        cursorY = await this.prepareNewPage(doc, layoutContext.headerState);
        cursorY += sectionSpacing;
        const descriptionResult = await this.renderProductDescription(
          doc,
          descriptionHtml,
          cursorY,
          layoutContext
        );
        cursorY = descriptionResult.cursorY;
        if (descriptionResult.hasContent) {
          cursorY = await this.ensureSectionGap(doc, cursorY, descriptionToTableSpacing, layoutContext);
        }
      }

      if (specRows.length) {
        cursorY = await this.renderSpecificationTable(doc, specRows, cursorY, layoutContext);
      }

      this.renderFooter(doc, {
        margin,
        pageWidth,
        pageHeight,
      });

      return doc.output('arraybuffer');
    }

    async prepareSwatchAsset(swatch) {
      if (!swatch) {
        return null;
      }

      if (swatch.imageElement) {
        try {
          const prepared = await this.prepareImage(swatch.imageElement, {
            mimeType: 'image/png',
            maxWidth: 256,
            quality: 0.92,
          });
          if (prepared?.dataUrl) {
            return { type: 'image', dataUrl: prepared.dataUrl, format: prepared.format || 'PNG' };
          }
        } catch (error) {
          console.warn('[Datasheet] Unable to prepare swatch image element', error);
        }
      }

      const imageUrls = Array.from(
        new Set([swatch.imageUrl, swatch.fallbackImageUrl].map((url) => this.ensureImageWidth(url, 256)).filter(Boolean))
      );
      for (const imageUrl of imageUrls) {
        try {
          if (this.cachedImages.has(imageUrl)) {
            const cached = this.cachedImages.get(imageUrl);
            if (cached?.dataUrl) {
              return { type: 'image', dataUrl: cached.dataUrl, format: cached.format || 'PNG' };
            }
          }

          const fallback = await this.prepareImageFallback(imageUrl, {
            mimeType: 'image/png',
            maxWidth: 256,
            quality: 0.92,
            preserveOriginal: true,
          });
          if (fallback?.dataUrl) {
            this.cachedImages.set(imageUrl, fallback);
            return { type: 'image', dataUrl: fallback.dataUrl, format: fallback.format || 'PNG' };
          }

          const image = await this.loadImage(imageUrl);
          const prepared = this.drawImage(image, {
            mimeType: 'image/png',
            maxWidth: 256,
            quality: 0.92,
          });
          if (prepared?.dataUrl) {
            this.cachedImages.set(imageUrl, prepared);
            return { type: 'image', dataUrl: prepared.dataUrl, format: prepared.format || 'PNG' };
          }
        } catch (error) {
          if (this.isSecurityError(error)) {
            try {
              const securityFallback = await this.prepareImageFallback(imageUrl, {
                mimeType: 'image/png',
                maxWidth: 256,
                quality: 0.92,
                preserveOriginal: true,
              });
              if (securityFallback?.dataUrl) {
                this.cachedImages.set(imageUrl, securityFallback);
                return { type: 'image', dataUrl: securityFallback.dataUrl, format: securityFallback.format || 'PNG' };
              }
            } catch (fallbackError) {
              console.warn('[Datasheet] Swatch fallback failed', fallbackError);
            }
          }
          console.warn('[Datasheet] Unable to load swatch image', error);
        }
      }

      if (swatch.color && !imageUrls.length) {
        const rgb = this.colorStringToRgb(swatch.color);
        if (rgb) {
          return { type: 'color', rgb };
        }
      }

      return null;
    }

    drawSwatch(doc, x, y, size, swatch) {
      if (!swatch || !Number.isFinite(size) || size <= 0) {
        return;
      }

      const dimension = size;

      if (swatch.type === 'image' && swatch.dataUrl) {
        try {
          doc.addImage(swatch.dataUrl, swatch.format || 'PNG', x, y, dimension, dimension);
        } catch (error) {
          console.warn('[Datasheet] Unable to render swatch image', error);
        }
      } else if (swatch.type === 'color' && Array.isArray(swatch.rgb)) {
        const [r, g, b] = swatch.rgb;
        doc.setFillColor(r, g, b);
        doc.rect(x, y, dimension, dimension, 'F');
      }

      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.6);
      doc.rect(x, y, dimension, dimension);
      doc.setDrawColor(0, 0, 0);
      doc.setFillColor(0, 0, 0);
    }

    async renderHeader(doc, { margin, pageWidth, logo, headingText, sku }) {
      const headerState = {
        margin,
        pageWidth,
        headingText: headingText || 'Product Datasheet',
        sku: sku || '',
        logo,
        headerSpacing: 40,
        maxLogoWidth: 160,
        height: 0,
        contentTop: margin,
      };

      await this.drawHeaderOnPage(doc, headerState);
      return headerState;
    }

    async drawHeaderOnPage(doc, headerState) {
      const { margin, pageWidth, headingText, headerSpacing, maxLogoWidth, logo, sku } = headerState;

      const canRenderLogo =
        logo && ((logo.svgText && logo.svgText.length) || (logo.bitmap && logo.bitmap.dataUrl));
      const logoDimensions = canRenderLogo ? this.calculateLogoDimensions(logo, maxLogoWidth) : { width: 0, height: 0 };

      doc.setFont('helvetica', 'bold');
      const headingFontSize = 14;
      doc.setFontSize(headingFontSize);
      const headingLineHeight = headingFontSize * 1.2;

      const skuValue = sku != null ? String(sku).trim() : '';
      const skuFontSize = 10;
      const skuLineHeight = skuFontSize * 1.3;
      const skuGap = skuValue ? 4 : 0;

      let textBlockHeight = headingLineHeight;
      if (skuValue) {
        textBlockHeight += skuGap + skuLineHeight;
      }

      const headerBlockHeight = Math.max(logoDimensions.height || 0, textBlockHeight);
      const blockTop = margin;

      if (canRenderLogo && (logoDimensions.width || 0) > 0 && (logoDimensions.height || 0) > 0) {
        const logoY = blockTop + (headerBlockHeight - logoDimensions.height) / 2;
        await this.drawLogo(doc, logo, {
          x: margin,
          y: logoY,
          maxWidth: maxLogoWidth,
          dimensionsOverride: logoDimensions,
        });
      }

      const textTop = blockTop + (headerBlockHeight - textBlockHeight) / 2;
      doc.text(headingText, pageWidth - margin, textTop, { align: 'right', baseline: 'top' });

      if (skuValue) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(skuFontSize);
        doc.text(skuValue, pageWidth - margin, textTop + headingLineHeight + skuGap, {
          align: 'right',
          baseline: 'top',
        });
      }

      const dividerY = blockTop + headerBlockHeight + 6;
      doc.setDrawColor(210, 210, 210);
      doc.setLineWidth(0.4);
      doc.line(margin, dividerY, pageWidth - margin, dividerY);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);

      const totalHeight = headerBlockHeight + headerSpacing;
      headerState.height = totalHeight;
      headerState.contentTop = margin + totalHeight;

      return totalHeight;
    }

    async prepareNewPage(doc, headerState) {
      doc.addPage();
      await this.drawHeaderOnPage(doc, headerState);
      return headerState.contentTop;
    }

    async ensureSectionGap(doc, cursorY, spacing, { pageHeight, margin, headerState }) {
      if (!spacing || spacing < 0) {
        return cursorY;
      }

      const safeContentBottom = pageHeight - margin - 40;
      if (cursorY + spacing > safeContentBottom) {
        cursorY = await this.prepareNewPage(doc, headerState);
      }

      cursorY += spacing;

      return cursorY;
    }

    calculateLogoDimensions(logo, maxWidth) {
      if (!logo) {
        return { width: 0, height: 0 };
      }

      const fallbackWidth = 512;
      const fallbackHeight = 150;
      const naturalWidth = Number(logo.width) || fallbackWidth;
      const naturalHeight = Number(logo.height) || fallbackHeight;
      const scale = maxWidth ? Math.min(1, maxWidth / naturalWidth) : 1;
      const drawWidth = naturalWidth * scale;
      const drawHeight = naturalHeight * scale;

      return { width: drawWidth, height: drawHeight };
    }

    async drawLogo(doc, logo, { x, y, maxWidth, dimensionsOverride }) {
      if (!logo) {
        return { width: 0, height: 0 };
      }

      const dimensions = dimensionsOverride || this.calculateLogoDimensions(logo, maxWidth);
      const drawWidth = dimensions.width;
      const drawHeight = dimensions.height;
      const baseWidth = Number(logo.width) || 512;
      const baseHeight = Number(logo.height) || 150;

      try {
        await this.ensureSvgPlugin();
        const svgElement = logo.svgText ? this.parseSvgMarkup(logo.svgText) : null;
        if (svgElement) {
          if (logo.viewBox) {
            svgElement.setAttribute('viewBox', logo.viewBox);
            } else if (!svgElement.getAttribute('viewBox')) {
              svgElement.setAttribute('viewBox', `0 0 ${baseWidth} ${baseHeight}`);
          }
          svgElement.setAttribute('width', `${drawWidth}`);
          svgElement.setAttribute('height', `${drawHeight}`);
          if (!svgElement.getAttribute('preserveAspectRatio')) {
            svgElement.setAttribute('preserveAspectRatio', 'xMidYMid meet');
          }

          if (typeof window.svg2pdf === 'function') {
            window.svg2pdf(svgElement, doc, { x, y, width: drawWidth, height: drawHeight });
            return { width: drawWidth, height: drawHeight };
          }

          if (typeof doc.svg === 'function') {
            await doc.svg(svgElement, { x, y, width: drawWidth, height: drawHeight });
            return { width: drawWidth, height: drawHeight };
          }
        }
      } catch (error) {
        console.warn('[Datasheet] Unable to render SVG logo vectorially', error);
      }

      if (logo.bitmap?.dataUrl) {
        doc.addImage(logo.bitmap.dataUrl, logo.bitmap.format || 'PNG', x, y, drawWidth, drawHeight);
        return { width: drawWidth, height: drawHeight };
      }

      return { width: 0, height: 0 };
    }

    parseSvgMarkup(svgText) {
      try {
        const parser = new DOMParser();
        const parsed = parser.parseFromString(svgText, 'image/svg+xml');
        const svgElement = parsed.documentElement;
        if (svgElement && svgElement.nodeName && svgElement.nodeName.toLowerCase() === 'svg') {
          return svgElement;
        }
      } catch (error) {
        console.warn('[Datasheet] Invalid SVG content', error);
      }
      return null;
    }

    async renderProductDescription(doc, html, startY, { margin, pageWidth, pageHeight, headerState }) {
      const blocks = this.parseDescriptionHtml(html);
      if (!blocks.length) {
        return { cursorY: startY, hasContent: false };
      }

      let cursorY = startY;
      let renderedSomething = false;
      const usableWidth = pageWidth - margin * 2;
      const baseFontSize = 10;
      const baseLineHeightFactor = 1.35;

      const applyBaseFont = () => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(baseFontSize);
        doc.setLineHeightFactor(baseLineHeightFactor);
      };

      const ensureRoom = async (height) => {
        if (cursorY + height > pageHeight - margin - 40) {
          cursorY = await this.prepareNewPage(doc, headerState);
          applyBaseFont();
        }
      };

      applyBaseFont();
      const baseLineHeight = doc.getFontSize() * doc.getLineHeightFactor();
      let firstBlock = true;

      for (const block of blocks) {
        if (!firstBlock) {
          cursorY += block.type === 'list' ? 4 : 6;
        }
        firstBlock = false;

        if (block.type === 'list') {
          for (let index = 0; index < block.items.length; index += 1) {
            const item = block.items[index];
            const orderValue = typeof item.order === 'number' ? item.order : index + 1;
            const bulletLabel = block.ordered ? `${orderValue}.` : '•';
            const bulletWidth = this.measureTextWidth(doc, bulletLabel, 'bold') + 6;
            const lines = this.layoutSegmentsIntoLines(
              doc,
              item.segments,
              Math.max(usableWidth - bulletWidth, usableWidth * 0.6)
            );
            if (!lines.length) {
              continue;
            }
            renderedSomething = true;
            for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
              const line = lines[lineIndex];
              await ensureRoom(baseLineHeight);
              if (lineIndex === 0) {
                doc.setFont('helvetica', this.mapFontStyle('bold'));
                doc.text(bulletLabel, margin, cursorY, { baseline: 'top' });
              }
              applyBaseFont();
              this.renderLineSegments(doc, line, margin + bulletWidth, cursorY);
              cursorY += baseLineHeight;
            }
            cursorY += 2;
          }
          applyBaseFont();
          continue;
        }

        const isHeading = Boolean(block.headingLevel);
        let lineHeight = baseLineHeight;
        if (isHeading) {
          const headingSize = Math.max(12, 18 - Math.min(block.headingLevel, 6));
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(headingSize);
          doc.setLineHeightFactor(1.25);
          lineHeight = doc.getFontSize() * doc.getLineHeightFactor();
        } else {
          applyBaseFont();
        }

        const lines = this.layoutSegmentsIntoLines(doc, block.segments, usableWidth);
        if (!lines.length) {
          applyBaseFont();
          continue;
        }

        renderedSomething = true;

        for (const line of lines) {
          await ensureRoom(lineHeight);
          this.renderLineSegments(doc, line, margin, cursorY);
          cursorY += lineHeight;
        }

        applyBaseFont();
      }

      return { cursorY, hasContent: renderedSomething };
    }

    parseDescriptionHtml(html) {
      if (typeof html !== 'string' || !html.trim()) {
        return [];
      }

      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const root = doc.body || doc.documentElement;
        const blocks = [];
        this.collectDescriptionBlocks(root, blocks);
        return blocks;
      } catch (error) {
        console.warn('[Datasheet] Unable to parse description HTML', error);
        return [];
      }
    }

    collectDescriptionBlocks(node, blocks) {
      if (!node || !node.childNodes) {
        return;
      }

      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === 3) {
          const text = child.textContent.replace(/\s+/g, ' ').trim();
          if (text) {
            blocks.push({ type: 'paragraph', segments: [{ text, style: 'normal' }] });
          }
          return;
        }

        if (child.nodeType !== 1) {
          return;
        }

        const tag = child.tagName ? child.tagName.toLowerCase() : '';
        if (!tag || tag === 'script' || tag === 'style') {
          return;
        }

        if (tag === 'ul' || tag === 'ol') {
          const items = [];
          Array.from(child.children || []).forEach((li, index) => {
            if (li.tagName && li.tagName.toLowerCase() === 'li') {
              const segments = this.collectInlineSegments(li, 'normal');
              if (segments.length) {
                items.push({ segments, order: index + 1 });
              }
            }
          });
          if (items.length) {
            blocks.push({ type: 'list', ordered: tag === 'ol', items });
          }
          return;
        }

        const blockTag = this.isDescriptionBlockTag(tag);
        if (blockTag) {
          const hasNestedBlocks = Array.from(child.children || []).some((grandchild) => {
            if (!grandchild.tagName) {
              return false;
            }
            return this.isDescriptionBlockTag(grandchild.tagName.toLowerCase()) || grandchild.tagName.toLowerCase() === 'ul' || grandchild.tagName.toLowerCase() === 'ol';
          });

          if (tag === 'li') {
            const segments = this.collectInlineSegments(child, 'normal');
            if (segments.length) {
              blocks.push({ type: 'list', ordered: false, items: [{ segments, order: 1 }] });
            }
            return;
          }

          if (hasNestedBlocks) {
            this.collectDescriptionBlocks(child, blocks);
            return;
          }

          const baseStyle = /^h[1-6]$/.test(tag) ? 'bold' : 'normal';
          const segments = this.collectInlineSegments(child, baseStyle);
          if (segments.length) {
            const level = /^h([1-6])$/.test(tag) ? parseInt(tag.replace(/\D+/g, ''), 10) : null;
            this.distributeSegmentsWithLists(segments, blocks, {
              headingLevel: level || undefined,
            });
          }
          return;
        }

        const segments = this.collectInlineSegments(child, 'normal');
        if (segments.length) {
          this.distributeSegmentsWithLists(segments, blocks, {});
          return;
        }

        this.collectDescriptionBlocks(child, blocks);
      });
    }

    isDescriptionBlockTag(tag) {
      if (!tag) {
        return false;
      }
      const normalized = tag.toLowerCase();
      return [
        'p',
        'div',
        'section',
        'article',
        'blockquote',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
      ].includes(normalized);
    }

    collectInlineSegments(node, baseStyle = 'normal') {
      if (!node) {
        return [];
      }
      const segments = [];
      Array.from(node.childNodes || []).forEach((child) => {
        segments.push(...this.collectInlineNode(child, baseStyle));
      });
      return this.mergeInlineSegments(segments);
    }

    distributeSegmentsWithLists(segments, blocks, baseOptions = {}) {
      if (!Array.isArray(segments) || !segments.length) {
        return;
      }

      let current = [];
      const pushParagraph = () => {
        const filtered = current.filter((segment) => typeof segment.type === 'undefined' && segment.text && segment.text.trim());
        if (!filtered.length) {
          current = [];
          return;
        }
        blocks.push({ type: 'paragraph', segments: current.slice(), ...baseOptions });
        current = [];
      };

      segments.forEach((segment) => {
        if (segment.type === 'embedded-list' && Array.isArray(segment.items)) {
          pushParagraph();
          blocks.push({ type: 'list', ordered: Boolean(segment.ordered), items: segment.items });
          return;
        }

        if (segment.type === 'break') {
          current.push(segment);
          return;
        }

        current.push(segment);
      });

      if (current.length) {
        pushParagraph();
      }
    }

    collectInlineNode(node, baseStyle) {
      if (!node) {
        return [];
      }

      if (node.nodeType === 3) {
        const text = node.textContent.replace(/\s+/g, ' ');
        if (!text.trim()) {
          return text.includes(' ') ? [{ text: ' ', style: baseStyle }] : [];
        }
        return [{ text, style: baseStyle }];
      }

      if (node.nodeType !== 1) {
        return [];
      }

      const tag = node.tagName ? node.tagName.toLowerCase() : '';
      if (tag === 'br') {
        return [{ type: 'break' }];
      }

      if (tag === 'strong' || tag === 'b') {
        return this.collectInlineSegments(node, this.mergeFontStyles(baseStyle, 'bold'));
      }
      if (tag === 'em' || tag === 'i' || tag === 'cite') {
        return this.collectInlineSegments(node, this.mergeFontStyles(baseStyle, 'italic'));
      }

      if (tag === 'sub' || tag === 'sup' || tag === 'span' || tag === 'u' || tag === 'mark' || tag === 'small' || tag === 'a') {
        return this.collectInlineSegments(node, baseStyle);
      }

      if (tag === 'ul' || tag === 'ol') {
        const items = [];
        Array.from(node.children || []).forEach((li, index) => {
          if (li.tagName && li.tagName.toLowerCase() === 'li') {
            const segments = this.collectInlineSegments(li, baseStyle);
            if (segments.length) {
              items.push({ segments, order: index + 1 });
            }
          }
        });
        if (items.length) {
          return [{ type: 'embedded-list', ordered: tag === 'ol', items }];
        }
        return [];
      }

      return this.collectInlineSegments(node, baseStyle);
    }

    mergeInlineSegments(segments) {
      const merged = [];
      segments.forEach((segment) => {
        if (segment.type === 'embedded-list') {
          merged.push(segment);
          return;
        }
        if (segment.type === 'break') {
          merged.push(segment);
          return;
        }
        if (!segment.text) {
          return;
        }
        if (merged.length && !merged[merged.length - 1].type && merged[merged.length - 1].style === segment.style) {
          merged[merged.length - 1].text += segment.text;
        } else {
          merged.push({ text: segment.text, style: segment.style });
        }
      });
      return merged;
    }

    mergeFontStyles(base, addition) {
      const baseLower = (base || 'normal').toLowerCase();
      const additionLower = (addition || 'normal').toLowerCase();
      const bold = baseLower.includes('bold') || additionLower.includes('bold');
      const italic = baseLower.includes('italic') || additionLower.includes('italic');
      if (bold && italic) {
        return 'bolditalic';
      }
      if (bold) {
        return 'bold';
      }
      if (italic) {
        return 'italic';
      }
      return 'normal';
    }

    layoutSegmentsIntoLines(doc, segments, maxWidth) {
      if (!segments || !segments.length || maxWidth <= 0) {
        return [];
      }

      const lines = [];
      let currentLine = [];
      let currentWidth = 0;

      const flushLine = () => {
        const trimmed = this.trimLineTrailingWhitespace(currentLine);
        if (trimmed.length) {
          lines.push(trimmed);
        }
        currentLine = [];
        currentWidth = 0;
      };

      segments.forEach((segment) => {
        if (segment.type === 'embedded-list' && segment.items) {
          flushLine();
          segment.items.forEach((item) => {
            lines.push(...this.layoutSegmentsIntoLines(doc, item.segments, maxWidth));
          });
          return;
        }

        if (segment.type === 'break') {
          flushLine();
          lines.push([]);
          return;
        }

        const tokens = segment.text.split(/(\s+)/).filter((token) => token.length > 0);
        tokens.forEach((token) => {
          const isWhitespace = /^\s+$/.test(token);
          if (isWhitespace && !currentLine.length) {
            return;
          }

          const width = this.measureTextWidth(doc, token, segment.style);
          if (!isWhitespace && currentLine.length && currentWidth + width > maxWidth) {
            flushLine();
            if (isWhitespace) {
              return;
            }
          }

          if (!currentLine.length && isWhitespace) {
            return;
          }

          currentLine.push({ text: token, style: segment.style });
          currentWidth += width;
        });
      });

      if (currentLine.length) {
        flushLine();
      }

      return lines;
    }

    trimLineTrailingWhitespace(line) {
      if (!Array.isArray(line)) {
        return [];
      }
      let end = line.length - 1;
      while (end >= 0) {
        if (line[end] && typeof line[end].text === 'string' && line[end].text.trim() === '') {
          end -= 1;
        } else {
          break;
        }
      }
      return line.slice(0, end + 1);
    }

    mapFontStyle(style) {
      switch ((style || 'normal').toLowerCase()) {
        case 'bold':
          return 'bold';
        case 'italic':
          return 'italic';
        case 'bolditalic':
        case 'italicbold':
          return 'bolditalic';
        default:
          return 'normal';
      }
    }

    measureTextWidth(doc, text, style) {
      if (!text) {
        return 0;
      }
      const previousFont = doc.getFont();
      const targetStyle = this.mapFontStyle(style);
      doc.setFont('helvetica', targetStyle);
      const width = doc.getTextWidth(text);
      const fallbackFontName = previousFont?.fontName || 'helvetica';
      const fallbackFontStyle = previousFont?.fontStyle || 'normal';
      doc.setFont(fallbackFontName, fallbackFontStyle);
      return width;
    }

    renderLineSegments(doc, segments, startX, y) {
      if (!Array.isArray(segments) || !segments.length) {
        return startX;
      }
      let offsetX = startX;
      segments.forEach((segment) => {
        if (typeof segment.text !== 'string' || !segment.text.length) {
          return;
        }
        const style = this.mapFontStyle(segment.style);
        const previousFont = doc.getFont();
        doc.setFont('helvetica', style);
        doc.text(segment.text, offsetX, y, { baseline: 'top' });
        offsetX += doc.getTextWidth(segment.text);
        const fallbackFontName = previousFont?.fontName || 'helvetica';
        const fallbackFontStyle = previousFont?.fontStyle || 'normal';
        doc.setFont(fallbackFontName, fallbackFontStyle);
      });
      return offsetX;
    }

    async renderImages(doc, images, startY, { margin, pageWidth, pageHeight, headerState }) {
      let cursorY = startY;
      const usableWidth = pageWidth - margin * 2;
      const gutter = 10;
      const columnCount = images.length > 4 ? 3 : 2;
      const columnWidth = (usableWidth - gutter * (columnCount - 1)) / columnCount;

      const rows = [];
      for (let i = 0; i < images.length; i += columnCount) {
        rows.push(images.slice(i, i + columnCount));
      }

      for (const row of rows) {
        let rowHeight = 0;
        const placements = row.map((image) => {
          const scale = Math.min(1, columnWidth / image.width);
          const drawWidth = image.width * scale;
          const drawHeight = image.height * scale;
          rowHeight = Math.max(rowHeight, drawHeight);
          return { image, drawWidth, drawHeight };
        });

        if (cursorY + rowHeight > pageHeight - margin - 40) {
          cursorY = await this.prepareNewPage(doc, headerState);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
          doc.setLineHeightFactor(1.2);
        }

        let offsetX = margin;
        placements.forEach((placement) => {
          doc.addImage(
            placement.image.dataUrl,
            placement.image.format || 'JPEG',
            offsetX,
            cursorY,
            placement.drawWidth,
            placement.drawHeight,
            undefined,
            'FAST',
          );
          offsetX += columnWidth + gutter;
        });

        cursorY += rowHeight + gutter;
      }

      return cursorY;
    }

    async renderSpecificationTable(doc, rows, startY, { margin, pageWidth, pageHeight, headerState }) {
      let cursorY = startY;
      const tableTopPadding = 16;
      if (tableTopPadding > 0) {
        const safeContentBottom = pageHeight - margin - 40;
        if (cursorY + tableTopPadding > safeContentBottom) {
          cursorY = await this.prepareNewPage(doc, headerState);
        }
        cursorY += tableTopPadding;
      }

      const tableWidth = pageWidth - margin * 2;
      const labelWidth = tableWidth * 0.32;
      const valueWidth = tableWidth - labelWidth;
      const padding = 4;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setLineHeightFactor(1.15);
      doc.setDrawColor(210, 210, 210);
      doc.setLineWidth(0.4);

      let drawTopBorder = true;

      for (let index = 0; index < rows.length; index += 1) {
        const row = rows[index];
        const labelLines = doc.splitTextToSize(row.label, labelWidth - padding * 2);
        const valueLines = doc.splitTextToSize(row.value, valueWidth - padding * 2);
        const lineCount = Math.max(labelLines.length, valueLines.length);
        const lineHeight = doc.getFontSize() * doc.getLineHeightFactor();
        const rowHeight = lineCount * lineHeight + padding * 2;

        if (cursorY + rowHeight > pageHeight - margin - 40) {
          cursorY = await this.prepareNewPage(doc, headerState);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
          doc.setLineHeightFactor(1.15);
          doc.setDrawColor(210, 210, 210);
          doc.setLineWidth(0.4);
          drawTopBorder = true;
        }

        if (drawTopBorder) {
          doc.line(margin, cursorY, pageWidth - margin, cursorY);
          drawTopBorder = false;
        }

        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'bold');
        doc.text(labelLines, margin + padding, cursorY + padding, { baseline: 'top' });

        doc.setFont('helvetica', 'normal');
        doc.text(valueLines, margin + labelWidth + padding, cursorY + padding, { baseline: 'top' });

        doc.line(margin, cursorY + rowHeight, pageWidth - margin, cursorY + rowHeight);

        cursorY += rowHeight;
      }

      return cursorY;
    }

    renderFooter(doc, { margin, pageWidth }) {
      let formattedDate;
      try {
        const formatter = new Intl.DateTimeFormat('en-GB');
        formattedDate = formatter.format(new Date());
      } catch (error) {
        formattedDate = new Date().toISOString().slice(0, 10);
      }
      const footerLeft = '© Ghidini 1849 | ghidini1849.com | info@ghidini1849.com';
      const disclaimerText = 'Technical information and product data may be updated at any time to improve quality, function, or performance. Product images are indicative: colors and details may vary due to craftsmanship, lighting, or display differences. Images and colors are not part of any contract or warranty.';

      const pageCount = doc.getNumberOfPages();
      for (let pageIndex = 1; pageIndex <= pageCount; pageIndex += 1) {
        doc.setPage(pageIndex);
        const height = doc.internal.pageSize.getHeight();
        const footerTop = height - margin - 24;
        const firstLineY = footerTop + 12;
        const secondLineY = firstLineY + 14;

        doc.setDrawColor(210, 210, 210);
        doc.setLineWidth(0.4);
        doc.line(margin, footerTop, pageWidth - margin, footerTop);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        doc.text(footerLeft, margin, firstLineY);

        const dateWidth = doc.getTextWidth(formattedDate);
        const dateX = pageWidth - margin - dateWidth;
        doc.text(formattedDate, dateX, firstLineY);

        doc.setFontSize(7);
        doc.setTextColor(102, 102, 102);
        const disclaimerLines = doc.splitTextToSize(disclaimerText, pageWidth - margin * 2);
        doc.text(disclaimerLines, margin, secondLineY);
      }
    }

    getFilename(variant, button) {
      const skuSource = (variant.sku || button.dataset.variantSku || '').trim();
      const safeSku = skuSource ? skuSource.replace(/[\\/:*?"<>|]+/g, '-') : `variant-${variant.id}`;
      return `${safeSku} Ghidini 1849.pdf`;
    }

    triggerDownload(arrayBuffer, filename) {
      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }

    async prepareImage(imgElement, options = {}) {
      const source = this.resolveImageSource(imgElement);
      if (!source) {
        return null;
      }

      if (this.cachedImages.has(source)) {
        return this.cachedImages.get(source);
      }

      try {
        const image = await this.loadImage(source);
        const prepared = this.drawImage(image, options);
        this.cachedImages.set(source, prepared);
        return prepared;
      } catch (error) {
        if (this.isSecurityError(error)) {
          const fallback = await this.prepareImageFallback(source, options);
          if (fallback) {
            this.cachedImages.set(source, fallback);
            return fallback;
          }
        }
        throw error;
      }
    }

    resolveImageSource(imgElement) {
      const srcset = imgElement.getAttribute('data-srcset') || imgElement.getAttribute('srcset') || '';
      if (srcset) {
        const candidates = srcset.split(',').map((item) => item.trim()).filter(Boolean);
        let bestUrl = null;
        let bestWidth = 0;
        candidates.forEach((candidate) => {
          const [url, size] = candidate.split(' ');
          const width = parseInt(size, 10);
          if (!Number.isNaN(width) && width > bestWidth) {
            bestWidth = width;
            bestUrl = url;
          }
        });
        if (bestUrl) {
          return this.ensureImageWidth(bestUrl, 1600);
        }
      }

      const currentSrc = imgElement.currentSrc || imgElement.src;
      return this.ensureImageWidth(currentSrc, 1600);
    }

    ensureImageWidth(url, width) {
      if (!url) {
        return null;
      }
      const normalizedUrl = String(url).replace(/&amp;/g, '&').trim();
      try {
        const parsed = new URL(normalizedUrl, window.location.origin);
        if (!parsed.searchParams.has('width')) {
          parsed.searchParams.set('width', `${width}`);
        }
        return parsed.toString();
      } catch (error) {
        return normalizedUrl;
      }
    }

    loadImage(url, options = {}) {
      return new Promise((resolve, reject) => {
        const image = new Image();
        const useCrossOrigin = options.crossOrigin !== false && !this.isSameOriginUrl(url);
        if (!/^data:/i.test(url) && useCrossOrigin) {
          image.crossOrigin = 'anonymous';
        }
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error(`Unable to load image: ${url}`));
        image.src = url;
      });
    }

    isSameOriginUrl(url) {
      if (!url || /^data:/i.test(url)) {
        return true;
      }
      try {
        return new URL(url, window.location.href).origin === window.location.origin;
      } catch (error) {
        return false;
      }
    }

    async prepareImageFallback(source, options = {}) {
      if (!source || /^data:/i.test(source)) {
        return null;
      }

      if (typeof FileReader !== 'function') {
        return null;
      }

      try {
        const blob = await this.fetchImageBlob(source);
        const dataUrl = await this.blobToDataUrl(blob);
        if (!dataUrl) {
          return null;
        }

        if (options.preserveOriginal) {
          const mimeType = String(blob.type || '').toLowerCase();
          const format = mimeType.includes('png') ? 'PNG' : 'JPEG';
          return { dataUrl, width: 0, height: 0, format };
        }

        const image = await this.loadImage(dataUrl);
        try {
          return this.drawImage(image, options);
        } catch (drawError) {
          console.warn('[Datasheet] Fallback image resize failed', drawError);
          const width = image.naturalWidth || image.width || 0;
          const height = image.naturalHeight || image.height || 0;
          const fallbackFormat = options.mimeType === 'image/png' ? 'PNG' : 'JPEG';
          return { dataUrl, width, height, format: fallbackFormat };
        }
      } catch (error) {
        console.warn('[Datasheet] Unable to fetch swatch image for Safari compatibility', error);
        return null;
      }
    }

    async fetchImageBlob(source) {
      if (typeof fetch === 'function') {
        try {
          const fetchOptions = this.isSameOriginUrl(source)
            ? { credentials: 'same-origin', cache: 'force-cache' }
            : { mode: 'cors', credentials: 'omit', cache: 'force-cache' };
          const response = await fetch(source, fetchOptions);
          if (!response.ok) {
            throw new Error(`Failed to fetch image: ${source}`);
          }
          return response.blob();
        } catch (error) {
          if (typeof XMLHttpRequest !== 'function') {
            throw error;
          }
          console.warn('[Datasheet] Fetch image failed, trying XMLHttpRequest fallback', error);
        }
      }

      return this.fetchImageBlobWithXhr(source);
    }

    fetchImageBlobWithXhr(source) {
      return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        request.open('GET', source, true);
        request.responseType = 'blob';
        request.onload = () => {
          if (request.status >= 200 && request.status < 300 && request.response) {
            resolve(request.response);
            return;
          }
          reject(new Error(`Failed to fetch image with XMLHttpRequest: ${source}`));
        };
        request.onerror = () => reject(new Error(`Unable to fetch image with XMLHttpRequest: ${source}`));
        request.send();
      });
    }

    blobToDataUrl(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Failed to convert image blob to data URL'));
        reader.readAsDataURL(blob);
      });
    }

    isSecurityError(error) {
      if (!error) {
        return false;
      }
      const name = error.name ? String(error.name) : '';
      if (name === 'SecurityError') {
        return true;
      }
      const message = error.message ? String(error.message).toLowerCase() : '';
      return (
        message.includes('securityerror') ||
        message.includes('tainted canvas') ||
        message.includes('cross-origin') ||
        message.includes('insecure')
      );
    }

    drawImage(image, { mimeType = 'image/jpeg', maxWidth = 1500, quality = 0.85 } = {}) {
      const naturalWidth = image.naturalWidth || image.width || maxWidth;
      const naturalHeight = image.naturalHeight || image.height || maxWidth;
      const scale = Math.min(1, maxWidth / naturalWidth);
      const width = Math.max(1, Math.round(naturalWidth * scale));
      const height = Math.max(1, Math.round(naturalHeight * scale));
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      context.imageSmoothingQuality = 'high';
      context.drawImage(image, 0, 0, width, height);
      const dataUrl = canvas.toDataURL(mimeType, quality);
      canvas.width = 0;
      canvas.height = 0;
      const format = mimeType === 'image/png' ? 'PNG' : 'JPEG';
      return { dataUrl, width, height, format };
    }

    async loadLogoImage() {
      if (!this.logoPromise) {
        this.logoPromise = (async () => {
          const fallbackSvgUrl = 'https://cdn.shopify.com/s/files/1/0557/1071/1987/files/ghidini-1849-logo-black.svg?v=1676482467';

          let svgText = '';
          let viewBox = '';
          let width = 0;
          let height = 0;

          try {
            const response = await fetch(fallbackSvgUrl, { credentials: 'omit', cache: 'force-cache' });
            if (response.ok) {
              svgText = await response.text();
              const svgElement = this.parseSvgMarkup(svgText);
              if (svgElement) {
                viewBox = svgElement.getAttribute('viewBox') || viewBox;
                const widthAttr = svgElement.getAttribute('width');
                const heightAttr = svgElement.getAttribute('height');
                const parsedWidth = widthAttr ? parseFloat(widthAttr) : NaN;
                const parsedHeight = heightAttr ? parseFloat(heightAttr) : NaN;
                if (!Number.isNaN(parsedWidth) && parsedWidth > 0) {
                  width = parsedWidth;
                }
                if (!Number.isNaN(parsedHeight) && parsedHeight > 0) {
                  height = parsedHeight;
                }
                if (viewBox) {
                  const parts = viewBox.split(/\s+/).map((value) => Number(value));
                  if (parts.length === 4) {
                    if (!width || width <= 0) {
                      width = parts[2];
                    }
                    if (!height || height <= 0) {
                      height = parts[3];
                    }
                  }
                }
              }
            } else {
              console.warn('[Datasheet] Logo SVG fetch failed with status', response.status);
            }
          } catch (error) {
            console.warn('[Datasheet] Unable to fetch SVG logo', error);
          }

          let bitmap = null;
          try {
            const image = await this.loadImage(fallbackSvgUrl);
            bitmap = this.drawImage(image, { mimeType: 'image/png', maxWidth: 2400, quality: 1 });
            if (!width) {
              width = image.naturalWidth || image.width || bitmap.width;
            }
            if (!height) {
              height = image.naturalHeight || image.height || bitmap.height;
            }
          } catch (error) {
            console.warn('[Datasheet] Unable to create bitmap fallback for logo', error);
          }

          if (!width) {
            width = 512;
          }
          if (!height) {
            height = 150;
          }

          if (!svgText) {
            svgText = '';
          }

          return {
            width,
            height,
            viewBox,
            svgText,
            bitmap,
          };
        })();
      }

      return this.logoPromise;
    }

    getVariantSkuDisplay(variant) {
      const rawSku = (variant?.sku || '').trim();
      if (rawSku) {
        return rawSku;
      }
      if (variant?.id) {
        return `${variant.id}`;
      }
      return '';
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    const generator = new ProductDatasheetGenerator();
    generator.init();
  });
})();

(() => {
  const hydrateSizeChart = async (owner) => {
    if (!owner || owner.hasAttribute('data-size-chart-hydrated') || owner.dataset.sizeChartLoading === 'true') {
      return;
    }

    const content = owner.querySelector('[data-size-chart-content]');
    const pageUrl = content?.dataset.sizeChartUrl;
    if (!content || !pageUrl) {
      return;
    }

    const fallback = content.querySelector('[data-size-chart-fallback]')?.cloneNode(true);
    const loading = document.createElement('p');
    loading.textContent = content.dataset.sizeChartLoading || '';
    content.replaceChildren(loading);
    content.setAttribute('aria-busy', 'true');
    owner.dataset.sizeChartLoading = 'true';

    try {
      const response = await fetch(pageUrl, { credentials: 'same-origin' });
      if (!response.ok) {
        throw new Error(`Unable to load size chart: ${response.status}`);
      }

      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const source = page.querySelector('.hdt-section-main-page .hdt-main-area');
      if (!source) {
        throw new Error('Unable to find size chart content');
      }

      content.replaceChildren(...document.importNode(source, true).childNodes);
      owner.setAttribute('data-size-chart-hydrated', 'true');
    } catch (error) {
      if (fallback) {
        content.replaceChildren(fallback);
      }
    } finally {
      content.removeAttribute('aria-busy');
      delete owner.dataset.sizeChartLoading;
    }
  };

  document.addEventListener('click', (event) => {
    const modalTrigger = event.target.closest('button[aria-controls]');
    const modalId = modalTrigger?.getAttribute('aria-controls');
    const modal = modalId ? document.getElementById(modalId) : null;

    if (modal?.matches('.hdt-product__modal.hdt-modal__size-chart')) {
      hydrateSizeChart(modal);
      return;
    }

    const drawerTrigger = event.target.closest('[data-product-detail-trigger]');
    const drawerKey = drawerTrigger?.dataset.productDetailTrigger;
    const drawer = drawerKey
      ? document.querySelector(`details[data-product-detail-drawer][data-product-detail-key="${CSS.escape(drawerKey)}"]`)
      : null;
    if (drawer?.classList.contains('hdt-product-detail-drawer--size-guide')) {
      hydrateSizeChart(drawer);
    }
  }, true);
})();

(function() {
  const quantitySelector = '.hdt-sticky-atc__qty-selector';
  const buttonSelector = `${quantitySelector} button[name="plus"], ${quantitySelector} button[name="minus"]`;

  const getLimit = (value, fallback) => {
    if (value === null || value === '') {
      return fallback;
    }
    const number = parseInt(value, 10);
    return Number.isNaN(number) ? fallback : number;
  };

  const updateButtonState = (wrapper, input) => {
    const value = getLimit(input.value, 1);
    const min = getLimit(input.min, 1);
    const max = getLimit(input.max, 9999);
    const minus = wrapper.querySelector('button[name="minus"]');
    const plus = wrapper.querySelector('button[name="plus"]');

    if (minus) {
      minus.classList.toggle('disabled', value <= min);
    }
    if (plus) {
      plus.classList.toggle('disabled', value >= max);
    }
  };

  const handleButtonClick = (event) => {
    const button = event.currentTarget;
    const wrapper = button.closest('hdt-quantity-wrapp');
    const input = wrapper ? wrapper.querySelector('input[name="quantity"]') : null;
    if (!wrapper || !input) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const previousValue = input.value;
    const currentValue = getLimit(input.value, getLimit(input.min, 1));
    const step = getLimit(input.step, 1);
    const min = getLimit(input.min, 1);
    const max = getLimit(input.max, 9999);

    if (button.name === 'plus') {
      input.value = Math.min(currentValue + step, max);
    } else {
      input.value = Math.max(currentValue - step, min);
    }

    updateButtonState(wrapper, input);
    if (previousValue !== input.value) {
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  const initStickyQuantity = () => {
    document.querySelectorAll(buttonSelector).forEach((button) => {
      if (button.dataset.stickyQuantityReady === 'true') {
        return;
      }
      button.dataset.stickyQuantityReady = 'true';
      button.addEventListener('click', handleButtonClick, true);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStickyQuantity);
  } else {
    initStickyQuantity();
  }
})();

(function() {
  const menuSelector = '[data-sticky-variant-menu]';
  const optionSelector = '[data-sticky-variant-option]';

  const updateMenuState = (menu, option) => {
    if (!menu || !option) {
      return;
    }

    menu.querySelectorAll(optionSelector).forEach((button) => {
      const isSelected = button === option;
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });

    const summaryText = menu.querySelector('[data-sticky-variant-summary-text]');
    const summarySwatch = menu.querySelector('[data-sticky-variant-summary-swatch]');
    if (summaryText) {
      summaryText.textContent = option.dataset.variantLabel || option.textContent.trim();
    }
    if (summarySwatch) {
      const swatchStyle = option.dataset.variantSwatchStyle;
      const imageUrl = option.dataset.variantImage;
      summarySwatch.replaceChildren();
      if (swatchStyle) {
        const swatch = document.createElement('span');
        swatch.className = 'hdt-sticky-atc__variant-swatch-pattern';
        swatch.setAttribute('style', swatchStyle);
        summarySwatch.appendChild(swatch);
      } else if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = '';
        image.loading = 'lazy';
        image.width = 36;
        image.height = 36;
        summarySwatch.appendChild(image);
      }
    }
  };

  const initStickyVariantMenus = () => {
    document.querySelectorAll(menuSelector).forEach((menu) => {
      const form = menu.closest('form');
      const select = form ? form.querySelector('.hdt-sticky-atc__native-select') : null;
      if (!select) {
        return;
      }

      menu.querySelectorAll(optionSelector).forEach((option) => {
        option.addEventListener('click', () => {
          const variantId = option.dataset.variantId;
          if (!variantId || option.disabled) {
            return;
          }
          select.value = variantId;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          updateMenuState(menu, option);
          menu.removeAttribute('open');
        });
      });

      select.addEventListener('change', () => {
        const option = menu.querySelector(`${optionSelector}[data-variant-id="${select.value}"]`);
        updateMenuState(menu, option);
      });
    });

    document.addEventListener('click', (event) => {
      document.querySelectorAll(`${menuSelector}[open]`).forEach((menu) => {
        if (!menu.contains(event.target)) {
          menu.removeAttribute('open');
        }
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStickyVariantMenus);
  } else {
    initStickyVariantMenus();
  }
})();

(function() {
  const drawerSelector = 'details[data-product-detail-drawer]';
  const closeSelector = '[data-product-detail-close]';
  const triggerSelector = '[data-product-detail-trigger]';
  const reviewTriggerSelector = '.jdgm-preview-badge, a[href*="#judgeme_product_reviews"]';
  const reviewTriggerScopeSelector = '.hdt-product-info__item, .hdt-product__summary, .hdt-product-info, .hdt-product-info__list';
  const openClass = 'hdt-product-detail-drawer-open';
  const activeClass = 'is-active';
  const closingClass = 'is-closing';
  const closeDuration = 340;

  const getActiveDrawers = () => Array.from(document.querySelectorAll(`${drawerSelector}[open], ${drawerSelector}.${closingClass}`));
  const getOpenDrawers = () => Array.from(document.querySelectorAll(`${drawerSelector}[open]`));

  const loadProductBrandMedia = (drawer) => {
    const media = drawer?.querySelector('template[data-product-brand-media]');
    if (media) media.replaceWith(media.content.cloneNode(true));
  };

  const loadReviewCatalogLocalization = (drawer) => {
    const isReviewDrawer = drawer?.getAttribute('data-product-detail-key') === 'reviews';
    if (!isReviewDrawer) return;
    const context = drawer.querySelector('[data-hdt-judgeme-catalog-module-url]');
    const moduleUrl = context?.dataset.hdtJudgemeCatalogModuleUrl;
    if (!moduleUrl || context.hdtJudgemeCatalogPromise) return;
    context.hdtJudgemeCatalogPromise = import(moduleUrl)
      .then((module) => module.startJudgeMeCatalogLocalization?.({ contextNode: context }))
      .catch(() => null);
  };

  const openDrawer = (drawer) => {
    if (!drawer) {
      return;
    }

    loadProductBrandMedia(drawer);
    loadReviewCatalogLocalization(drawer);
    drawer.classList.remove(closingClass);
    if (!drawer.open) {
      drawer.setAttribute('open', '');
    }
    window.requestAnimationFrame(() => {
      drawer.classList.add(activeClass);
      updateDocumentState();
    });
    getOpenDrawers().forEach((openDrawerItem) => {
      if (openDrawerItem !== drawer) {
        closeDrawer(openDrawerItem);
      }
    });
  };

  const findDrawerByKey = (key) => {
    if (!key) {
      return null;
    }

    return document.querySelector(`${drawerSelector}[data-product-detail-key="${key}"]`);
  };

  const updateDocumentState = () => {
    document.documentElement.classList.toggle(openClass, getActiveDrawers().length > 0);
  };

  const handleReviewTriggerClick = (event) => {
    const reviewTrigger = event.target.closest(reviewTriggerSelector);
    if (!reviewTrigger || reviewTrigger.closest('#judgeme_product_reviews, [data-product-detail-key="reviews"] .hdt-product-detail-drawer__body')) {
      return;
    }

    if (!reviewTrigger.closest(reviewTriggerScopeSelector) || reviewTrigger.closest('hdt-card-product, .hdt-card-product, product-recommendations')) {
      return;
    }

    const drawer = findDrawerByKey('reviews');
    if (!drawer) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    openDrawer(drawer);
  };

  const closeDrawer = (drawer) => {
    if (!drawer || drawer.classList.contains(closingClass)) {
      return;
    }

    drawer.classList.remove(activeClass);
    drawer.classList.add(closingClass);
    updateDocumentState();
    window.setTimeout(() => {
      drawer.removeAttribute('open');
      drawer.classList.remove(closingClass);
      updateDocumentState();
    }, closeDuration);
  };

  document.addEventListener('toggle', (event) => {
    const drawer = event.target.matches?.(drawerSelector) ? event.target : null;
    if (!drawer) {
      return;
    }

    if (drawer.open) {
      loadProductBrandMedia(drawer);
      loadReviewCatalogLocalization(drawer);
      drawer.classList.remove(closingClass);
      window.requestAnimationFrame(() => {
        drawer.classList.add(activeClass);
      });
      getOpenDrawers().forEach((openDrawer) => {
        if (openDrawer !== drawer) {
          closeDrawer(openDrawer);
        }
      });
    }
    updateDocumentState();
  }, true);

  document.addEventListener('click', handleReviewTriggerClick, true);

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest(triggerSelector);
    if (trigger) {
      const drawer = findDrawerByKey(trigger.dataset.productDetailTrigger);
      if (drawer) {
        event.preventDefault();
        openDrawer(drawer);
      }
      return;
    }

    const closeButton = event.target.closest(closeSelector);
    if (!closeButton) {
      return;
    }

    const drawer = closeButton.closest(drawerSelector);
    if (!drawer) {
      return;
    }

    event.preventDefault();
    closeDrawer(drawer);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') {
      return;
    }

    getOpenDrawers().forEach((drawer) => closeDrawer(drawer));
  });
})();

(function() {
  const bulbLinkSelector = '.hdt-product-summary-links__item--bulbs';
  const stackedClass = 'is-stacked';
  let updateFrame = null;

  const toNumber = (value) => {
    const number = Number.parseFloat(value);
    return Number.isFinite(number) ? number : 0;
  };

  const getFlexGap = (element) => {
    const styles = window.getComputedStyle(element);
    return toNumber(styles.columnGap || styles.gap);
  };

  const getInlineContentWidth = (label) => {
    const gap = getFlexGap(label);
    const children = Array.from(label.children).filter((child) => window.getComputedStyle(child).display !== 'none');
    const contentWidth = children.reduce((width, child) => {
      return width + Math.max(child.scrollWidth, child.getBoundingClientRect().width);
    }, 0);

    return contentWidth + Math.max(children.length - 1, 0) * gap;
  };

  const updateBulbLinkWrap = () => {
    document.querySelectorAll(bulbLinkSelector).forEach((item) => {
      const icon = item.querySelector('.hdt-product-summary-links__icon');
      const label = item.querySelector('.hdt-product-summary-links__label--stacked');

      if (!icon || !label) {
        return;
      }

      item.classList.remove(stackedClass);

      const availableWidth = item.getBoundingClientRect().width - icon.getBoundingClientRect().width - getFlexGap(item);
      const requiredWidth = getInlineContentWidth(label);

      item.classList.toggle(stackedClass, requiredWidth > availableWidth + 1);
    });
  };

  const scheduleBulbLinkWrapUpdate = () => {
    if (updateFrame) {
      window.cancelAnimationFrame(updateFrame);
    }

    updateFrame = window.requestAnimationFrame(() => {
      updateFrame = null;
      updateBulbLinkWrap();
    });
  };

  const initBulbLinkWrap = () => {
    updateBulbLinkWrap();

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(scheduleBulbLinkWrapUpdate);
      document.querySelectorAll(bulbLinkSelector).forEach((item) => resizeObserver.observe(item));
    }

    window.addEventListener('resize', scheduleBulbLinkWrapUpdate, { passive: true });
    window.addEventListener('orientationchange', scheduleBulbLinkWrapUpdate);
    document.addEventListener('shopify:section:load', scheduleBulbLinkWrapUpdate);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBulbLinkWrap);
  } else {
    initBulbLinkWrap();
  }
})();

(function() {
  const whatsappSelector = '[data-whatsapp-link]';
  const askTextareaSelector = 'textarea[data-product-title][data-product-url]';
  const contactDrawerSelector = '[data-product-contact-drawer]';
  const contactEmailSelector = '[data-product-contact-email]';
  const contactWhatsappSelector = '[data-product-contact-whatsapp]';
  const variantEvents = ['variant:update', 'variant:change', 'variant:changed', 'variant:selected'];
  let currentVariantId = null;
  let currentVariant = null;

  const buildVariantUrl = (baseUrl, origin, variantId) => {
    if (!baseUrl) {
      return '';
    }

    try {
      const resolved = new URL(baseUrl, origin || window.location.origin);
      if (variantId) {
        resolved.searchParams.set('variant', variantId);
      }
      return resolved.toString();
    } catch (error) {
      let fallback = baseUrl;
      if (origin && !/^https?:\/\//.test(baseUrl)) {
        fallback = `${origin.replace(/\/$/, '')}/${baseUrl.replace(/^\//, '')}`;
      }
      if (variantId) {
        fallback += (fallback.includes('?') ? '&' : '?') + `variant=${variantId}`;
      }
      return fallback;
    }
  };

  const updateWhatsAppLink = (variantId) => {
    document.querySelectorAll(whatsappSelector).forEach((link) => {
      const base = link.dataset.whatsappBase;
      const productUrl = link.dataset.productUrl;
      if (!base || !productUrl) {
        return;
      }
      const origin = link.dataset.shopUrl || link.dataset.origin || window.location.origin;
      const fullUrl = buildVariantUrl(productUrl, origin, variantId);
      link.href = `${base}${fullUrl}`;
    });
  };

  const updateAskQuestionForm = (variantId) => {
    document.querySelectorAll(askTextareaSelector).forEach((textarea) => {
      const title = textarea.dataset.productTitle;
      const productUrl = textarea.dataset.productUrl;
      if (!title || !productUrl) {
        return;
      }
      const origin = textarea.dataset.origin || window.location.origin;
      const fullUrl = buildVariantUrl(productUrl, origin, variantId);
      textarea.value = `${title} - ${fullUrl}`;
    });
  };

  const getProductQuantity = () => {
    const quantityInput = document.querySelector('form[action*="/cart/add"] [name="quantity"]');
    return quantityInput && quantityInput.value ? quantityInput.value : '1';
  };

  const buildContactMessage = (drawer, variantId, variant) => {
    const title = drawer.dataset.productTitle || document.querySelector('.hdt-product__title, [data-product-title]')?.textContent?.trim() || '';
    const productUrl = drawer.dataset.productUrl || window.location.pathname;
    const origin = drawer.dataset.shopUrl || window.location.origin;
    const fullUrl = buildVariantUrl(productUrl, origin, variantId);
    const sku = variant?.sku || drawer.dataset.selectedSku || '';
    const variantTitle = variant?.title || drawer.dataset.selectedVariantTitle || '';
    const intro = drawer.dataset.messageIntro || 'Product request';
    const parts = [
      intro,
      `Product: ${title}`
    ];

    if (variantTitle && variantTitle !== 'Default Title') {
      parts.push(`Variant: ${variantTitle}`);
    }
    if (sku) {
      parts.push(`SKU: ${sku}`);
    }
    parts.push(`Quantity: ${getProductQuantity()}`);
    parts.push(`URL: ${fullUrl}`);

    return parts.join('\n');
  };

  const updateProductContactLinks = (variantId, variant) => {
    document.querySelectorAll(contactDrawerSelector).forEach((drawer) => {
      const message = buildContactMessage(drawer, variantId, variant);
      const title = drawer.dataset.productTitle || '';
      const subject = drawer.dataset.emailSubject || `Product request${title ? ` - ${title}` : ''}`;
      const encodedMessage = encodeURIComponent(message);
      const email = drawer.querySelector(contactEmailSelector);
      const whatsapp = drawer.querySelector(contactWhatsappSelector);

      if (email) {
        email.href = `mailto:info@ghidini1849.com?subject=${encodeURIComponent(subject)}&body=${encodedMessage}`;
      }
      if (whatsapp) {
        whatsapp.href = `https://wa.me/390308971379?text=${encodedMessage}`;
      }
    });
  };

  const handleVariantEvent = (event) => {
    const variantId = event?.detail?.variant?.id || event?.detail?.variantId;
    if (!variantId) {
      return;
    }
    currentVariantId = variantId;
    currentVariant = event?.detail?.variant || null;
    updateWhatsAppLink(variantId);
    updateAskQuestionForm(variantId);
    updateProductContactLinks(currentVariantId, currentVariant);
  };

  const init = () => {
    const formInput = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    if (formInput && formInput.value) {
      currentVariantId = parseInt(formInput.value, 10);
    }
    updateWhatsAppLink(currentVariantId);
    updateAskQuestionForm(currentVariantId);
    updateProductContactLinks(currentVariantId);
    variantEvents.forEach((eventName) => {
      document.addEventListener(eventName, handleVariantEvent);
    });
    document.addEventListener('change', (event) => {
      if (event.target.matches('form[action*="/cart/add"] [name="quantity"]')) {
        updateProductContactLinks(currentVariantId, currentVariant);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();


// One observer owns purchase-bar visibility, including forms rendered after connection.
(() => {
  const init = () => {
    const bar = document.querySelector('hdt-sticky-btn-atc');
    const footer = document.querySelector('.shopify-section-group-footer-group');
    if (!bar || !footer || !('IntersectionObserver' in window)) return;
    let pending;
    const connect = () => {
      const form = document.getElementById(bar.getAttribute('form'));
      if (!form) return false;
      pending?.disconnect();
      let passedForm = form.getBoundingClientRect().bottom < 0;
      let footerVisible = footer.getBoundingClientRect().top < window.innerHeight;
      const update = () => {
        const noPick = document.querySelector('hdt-variant-picker[no-pick]');
        const active = passedForm && !footerVisible && !noPick;
        bar.toggleAttribute('data-purchase-visible', active);
        bar.toggleAttribute('data-footer-visible', footerVisible);
        document.body.classList.toggle('sticky-shown', active);
      };
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.target === form) passedForm = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          if (entry.target === footer) footerVisible = entry.isIntersecting;
        });
        update();
      });
      observer.observe(form);
      observer.observe(footer);
      update();
      return true;
    };
    if (!connect()) {
      pending = new MutationObserver(connect);
      pending.observe(bar.closest('.shopify-section') || document.body, { childList: true, subtree: true });
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
